import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Pill, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import FileUpload from "@/components/FileUpload";

export default function PrescriptionsPage() {
  const { user } = useAuth();
  const [rx, setRx] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [patient, setPatient] = useState("");
  const [items, setItems] = useState([{ _k: crypto.randomUUID(), medicine: "", dosage: "", duration: "", instructions: "" }]);
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    const [r, p] = await Promise.all([api.get("/prescriptions"), api.get("/patients")]);
    setRx(r.data); setPatients(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/prescriptions", { patient_id: patient, items, notes });
      toast.success("Prescription created");
      setOpen(false); setPatient(""); setItems([{ _k: crypto.randomUUID(), medicine: "", dosage: "", duration: "", instructions: "" }]); setNotes("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Doctors only");
    }
  };

  const addItem = () => setItems([...items, { medicine: "", dosage: "", duration: "", instructions: "" }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));

  const canCreate = user.role === "doctor" || user.role === "super_admin";

  return (
    <div className="space-y-5 fade-up" data-testid="prescriptions-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Prescriptions</h1>
          <p className="text-sm text-muted-foreground mt-1">{rx.length} prescriptions issued</p>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="new-rx-btn"><Plus className="size-4" /> New prescription</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>New prescription</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="rx-form">
                <div className="space-y-1.5">
                  <Label>Patient *</Label>
                  <Select value={patient} onValueChange={setPatient}>
                    <SelectTrigger><SelectValue placeholder="Select patient" /></SelectTrigger>
                    <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Medicines</Label>
                  {items.map((it, i) => (
                    <div key={it._k} className="grid grid-cols-12 gap-2 items-start p-2 border border-border rounded-md">
                      <Input className="col-span-4" placeholder="Medicine" value={it.medicine} onChange={(e) => updateItem(i, "medicine", e.target.value)} required />
                      <Input className="col-span-2" placeholder="Dosage" value={it.dosage} onChange={(e) => updateItem(i, "dosage", e.target.value)} required />
                      <Input className="col-span-2" placeholder="Duration" value={it.duration} onChange={(e) => updateItem(i, "duration", e.target.value)} required />
                      <Input className="col-span-3" placeholder="Instructions" value={it.instructions} onChange={(e) => updateItem(i, "instructions", e.target.value)} />
                      <Button type="button" variant="ghost" size="icon" className="col-span-1" onClick={() => removeItem(i)} disabled={items.length === 1}>
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={addItem}>+ Add medicine</Button>
                </div>
                <div className="space-y-1.5"><Label>Notes</Label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
                {patient ? (
                  <div className="border-t border-border pt-3">
                    <FileUpload patientId={patient} resourceType="prescription" label="Attach prescription file / scans" compact />
                  </div>
                ) : (
                  <div className="border-t border-border pt-3 text-[11px] text-muted-foreground">
                    Select a patient to attach files (PDFs, photos, scans).
                  </div>
                )}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button type="submit" data-testid="save-rx-btn">Save</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rx.map((r) => {
          const p = patients.find((x) => x.id === r.patient_id);
          return (
            <Card key={r.id}>
              <CardContent className="p-5">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-semibold">{p?.name || "—"}</div>
                    <div className="text-xs text-muted-foreground mono mt-0.5">{new Date(r.created_at).toLocaleString()}</div>
                  </div>
                  <Pill className="size-5 text-primary opacity-60" />
                </div>
                <ul className="mt-3 space-y-1">
                  {r.items.map((it, i) => (
                    <li key={`${r.id}-${i}-${it.medicine}`} className="text-sm">• <strong>{it.medicine}</strong> · {it.dosage} · {it.duration}</li>
                  ))}
                </ul>
                {r.notes && <div className="mt-3 text-xs text-muted-foreground border-t border-border pt-3">{r.notes}</div>}
              </CardContent>
            </Card>
          );
        })}
        {rx.length === 0 && (
          <Card className="md:col-span-2"><CardContent className="p-10 text-center text-muted-foreground">No prescriptions yet.</CardContent></Card>
        )}
      </div>
    </div>
  );
}
